// MUNYA OS · K6 § 7.11-ter · Indicador online/offline en topbar
// Estados: 🟢 online · 🟡 sin red · (con K3/K4 PWA queue: contador de cambios pendientes)

import { useEffect, useState, type ReactElement } from 'react';
import { CloudCheck, CloudWarning, CloudX } from '@phosphor-icons/react';

type Status = 'online' | 'offline' | 'reconnecting';

export function OnlineStatusChip(): ReactElement | null {
  const [status, setStatus] = useState<Status>(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online',
  );

  useEffect(() => {
    function onOnline(): void {
      setStatus('reconnecting');
      // Probe rápido al backend antes de declarar online "fiable"
      setTimeout(() => {
        fetch('/api/v1/health/live', { credentials: 'same-origin', cache: 'no-store' })
          .then((r) => {
            setStatus(r.ok ? 'online' : 'offline');
          })
          .catch(() => {
            setStatus('offline');
          });
      }, 800);
    }
    function onOffline(): void {
      setStatus('offline');
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (status === 'online') {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 h-6 px-1.5 rounded text-[10px] leading-none opacity-50 hover:opacity-80 transition"
        title="Conectado"
        aria-label="Online"
      >
        <CloudCheck size={11} weight="duotone" />
      </span>
    );
  }

  if (status === 'reconnecting') {
    return (
      <span
        className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-[10px] leading-none bg-[var(--mn-warn-bg)] text-[var(--mn-warn-fg)] motion-safe:animate-pulse"
        title="Reconectando…"
        aria-label="Reconectando"
      >
        <CloudWarning size={11} weight="fill" /> Reconectando…
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10px] leading-none bg-[var(--color-munya-destructive)]/15 text-[var(--color-munya-destructive)]"
      title="Sin conexión · los cambios no se guardan"
      aria-label="Offline"
    >
      <CloudX size={11} weight="fill" /> Sin red
    </span>
  );
}
